/**
 * ノードメニューの中に開くサブパネル。
 *
 * 「別のルートへ移す」一覧と「詳細…」の2つで、どちらもメニューの中に
 * 後から中身を作る。開いているのは常に1つだけにする。
 *
 * ここは DOM の組み立てとボタンの結線だけを行い、実際の操作は
 * 渡された関数に任せる。
 */

import { query, queryAll } from '../lib/dom.js';

/** モバイル幅（CSS の 640px 以下と揃える）で、本文内に置く編集メニューか。 */
export function isInlineNodeMenu(menu: Element | null): boolean {
  return !!menu?.closest('.has-inline-node-menu') && window.matchMedia('(max-width: 640px)').matches;
}

/** 操作列の隠れたボタンがある側に、横スクロールの手がかりを表示する。 */
export function updateNodeMenuScrollHints(menu: HTMLElement): void {
  const toolbar = menu.querySelector<HTMLElement>('.node-menu-toolbar');
  const visible = isInlineNodeMenu(menu) && toolbar && toolbar.clientWidth > 0;
  menu.classList.toggle('can-scroll-left', !!visible && toolbar.scrollLeft > 1);
  menu.classList.toggle(
    'can-scroll-right',
    !!visible && toolbar.scrollWidth - toolbar.clientWidth - toolbar.scrollLeft > 1
  );
}

/** キーボード表示中も、サブパネルを使えるビジュアルビューポート内に収める。 */
export function positionInlineNodeSubpanels(menu: HTMLElement): void {
  const inline = isInlineNodeMenu(menu);
  const rect = menu.getBoundingClientRect();
  const viewport = window.visualViewport;
  const viewTop = viewport?.offsetTop ?? 0;
  const viewBottom = viewTop + (viewport?.height ?? window.innerHeight);
  const headerBottom = document.querySelector('.topbar')?.getBoundingClientRect().bottom ?? 0;
  const above = rect.top - Math.max(viewTop, headerBottom) - 14;
  const below = viewBottom - rect.bottom - 14;
  for (const panel of queryAll<HTMLElement>('.node-move-target-panel', menu)) {
    if (!inline || panel.hidden) {
      panel.style.removeProperty('max-height');
      if (inline) panel.classList.remove('is-above');
      continue;
    }
    const preferredHeight = panel.classList.contains('node-detail-panel') ? 320 : 280;
    const useAbove = below < Math.min(preferredHeight, panel.scrollHeight) && above > below;
    panel.classList.toggle('is-above', useAbove);
    panel.style.maxHeight = `${Math.max(44, Math.min(preferredHeight, useAbove ? above : below))}px`;
  }
}

/** ごみ箱/完了ノートなど、移動先にできないノート名か。 */
export function isTrashNoteName(name) {
  return String(name || '').startsWith('ごみ箱/');
}

export function createNodeMenuPanels({
  state,
  t,
  apiGet,
  outlineEl,
  closeNodeMenus,
  shortNodeText,
  moveNodeUnderRoot,
  moveNodeToDone,
  moveNodeToNote,
  openNodeEditDialog,
  attachImageToNode,
  openNodeLinkDialog,
  visibleIds,
  mergeNodeUp,
  copyNodeToClipboard,
  pasteClipboardIntoNode,
  pasteExpandedIntoNode,
}) {
  function closeNodeMoveTargetPanels(exceptPanel?) {
    for (const panel of queryAll('.node-move-target-panel', outlineEl)) {
      if (exceptPanel && panel === exceptPanel) {
        continue;
      }
      panel.hidden = true;
      panel.classList.remove('is-above');
      panel.replaceChildren();
    }
  }

  // お気に入りのうち自分のノートだけを、移動先として並べる。現在のノートとごみ箱系は除く。
  async function fillFavoriteNotes(panel, slot, id) {
    if (id === null || state.offlineView) return;
    try {
      const data = await apiGet('api/favorites.php');
      if (panel.hidden || !slot.isConnected) return;
      const notes = (data.favorites || []).filter((favorite) => {
        return (
          favorite.is_own && Number(favorite.file_id) !== Number(state.activeFileId) && !isTrashNoteName(favorite.name)
        );
      });
      for (const note of notes) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'node-move-target-button';
        button.textContent = `📒 ${note.name}`;
        button.title = note.name;
        button.setAttribute('role', 'menuitem');
        button.addEventListener('click', async (event) => {
          event.stopPropagation();
          closeNodeMenus();
          await moveNodeToNote(id, Number(note.file_id));
        });
        slot.append(button);
      }
      if (notes.length > 0) {
        const separator = document.createElement('div');
        separator.className = 'menu-separator';
        separator.setAttribute('aria-hidden', 'true');
        slot.append(separator);
      }
    } catch (error) {
      console.error(error);
    }
  }

  function openNodeMoveTargetPanel(id, menu) {
    const panel = query('.node-move-target-panel:not(.node-detail-panel)', menu);
    if (!panel) {
      return;
    }

    const shouldOpen = panel.hidden;
    closeNodeMoveTargetPanels(panel);
    panel.classList.remove('is-above');
    panel.replaceChildren();
    panel.hidden = !shouldOpen;
    if (!shouldOpen) {
      return;
    }

    const node = state.nodes.get(id);
    const targetIds = state.rootIds.filter((targetId) => {
      return targetId !== id && targetId !== node?.parent_id && state.nodes.has(targetId);
    });

    const addButton = (label, title, handler) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'node-move-target-button';
      button.textContent = label;
      button.title = title;
      button.setAttribute('role', 'menuitem');
      button.addEventListener('click', async (event) => {
        event.stopPropagation();
        closeNodeMenus();
        await handler();
      });
      panel.append(button);
    };
    const addSeparator = () => {
      const separator = document.createElement('div');
      separator.className = 'menu-separator';
      separator.setAttribute('aria-hidden', 'true');
      panel.append(separator);
    };

    // お気に入りのノートは先に表示しておき、取得できたら先頭へ差し込む。
    const favoriteSlot = document.createElement('div');
    favoriteSlot.className = 'node-move-favorites';
    panel.append(favoriteSlot);

    if (!state.offlineView) {
      addButton(t('node_move_done_item'), t('node_move_done'), () => moveNodeToDone(id));
    }

    if (targetIds.length > 0 && !state.offlineView) {
      addSeparator();
    }
    for (const targetId of targetIds) {
      const target = state.nodes.get(targetId);
      addButton(`📍${shortNodeText(target.text)}`, target.text || t('markdown_empty'), () =>
        moveNodeUnderRoot(id, targetId)
      );
    }

    void fillFavoriteNotes(panel, favoriteSlot, moveNodeToNote ? id : null);

    panel.classList.remove('is-above');
    if (!isInlineNodeMenu(menu)) {
      const menuRect = menu.getBoundingClientRect();
      if (menuRect.bottom + 6 + 280 > window.innerHeight && menuRect.top > 280) {
        panel.classList.add('is-above');
      }
    }

    positionInlineNodeSubpanels(menu);
    query('.node-move-target-button', panel)?.focus({ preventScroll: isInlineNodeMenu(menu) });
  }

  // ノードメニューの [🔧 詳細…] から開くサブメニュー。
  // テキスト編集、画像添付、上のノードと結合、コピー、貼り付け、展開して貼り付けをまとめる。
  function openNodeDetailPanel(id, menu) {
    const panel = query('.node-detail-panel', menu);
    if (!panel) {
      return;
    }

    const shouldOpen = panel.hidden;
    closeNodeMoveTargetPanels(panel);
    panel.classList.remove('is-above');
    panel.replaceChildren();
    panel.hidden = !shouldOpen;
    if (!shouldOpen) {
      return;
    }

    const makeDetailButton = (label, title, handler, closeOnClick = true) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'node-move-target-button';
      button.textContent = label;
      button.title = title;
      button.setAttribute('role', 'menuitem');
      button.addEventListener('click', async (event) => {
        event.stopPropagation();
        if (closeOnClick) closeNodeMenus();
        await handler();
      });
      panel.append(button);
      return button;
    };

    const makeSeparator = () => {
      const separator = document.createElement('div');
      separator.className = 'menu-separator';
      separator.setAttribute('aria-hidden', 'true');
      panel.append(separator);
    };

    makeDetailButton(
      t('node_move_btn'),
      t('node_move_under_root'),
      () => {
        // 移動先一覧へ切り替える前に、消える詳細項目から同じノード内へフォーカスを退避する。
        query('.node-details-menu-item', menu)?.focus();
        openNodeMoveTargetPanel(id, menu);
      },
      false
    );
    makeDetailButton(t('node_done_btn'), t('node_move_done'), () => moveNodeToDone(id));
    makeSeparator();

    makeDetailButton(t('node_edit_dialog_btn'), t('node_edit'), () => openNodeEditDialog(id));
    makeDetailButton(t('node_attach_image_btn'), t('node_attach_image'), () => attachImageToNode(id));
    makeDetailButton(t('node_insert_link_btn'), t('node_insert_link'), () => openNodeLinkDialog(id));

    const ids = visibleIds();
    const canMergeUp = ids.indexOf(id) > 0;
    if (canMergeUp) {
      makeSeparator();
      makeDetailButton(t('node_merge_up_btn'), t('node_merge_up'), () => mergeNodeUp(id));
    }

    makeSeparator();

    makeDetailButton(t('node_copy_btn'), t('node_copy'), () => copyNodeToClipboard(id));
    makeDetailButton(t('node_paste_btn'), t('node_paste'), () => pasteClipboardIntoNode(id));
    makeDetailButton(t('node_paste_expanded_btn'), t('node_paste_expanded'), () => pasteExpandedIntoNode(id));

    panel.classList.remove('is-above');
    if (!isInlineNodeMenu(menu)) {
      const menuRect = menu.getBoundingClientRect();
      if (menuRect.bottom + 6 + 300 > window.innerHeight && menuRect.top > 300) {
        panel.classList.add('is-above');
      }
    }

    positionInlineNodeSubpanels(menu);
    query('.node-move-target-button', panel)?.focus({ preventScroll: isInlineNodeMenu(menu) });
  }

  // 現在のノードを直前の表示ノード（上のノード）と改行を挟んで結合する。

  return { closeNodeMoveTargetPanels, openNodeMoveTargetPanel, openNodeDetailPanel };
}
